import { compact, groupBy } from "lodash";

import { QUICK_SELECT_OPTIONS } from "@components/v2/date-picker/constants";
import {
  type QuickFilter,
  type QuickFilterRevenueConfig,
} from "@north-beam/nb-common";
import { formatDateRangeText } from "@shared/date-range-picker";

import { useBreakdownDimensions, useSalesV4Context } from "../hooks";

export const FilterTooltip = ({
  filter,
}: {
  filter: QuickFilter<QuickFilterRevenueConfig>;
}) => {
  const breakdownDimensions = useBreakdownDimensions();
  const { metrics } = useSalesV4Context();

  const formatDateRange = () => {
    if (typeof filter.config.dateRange === "string") {
      return QUICK_SELECT_OPTIONS.find(
        (e) => e.value === filter.config.dateRange,
      )?.label;
    }

    return formatDateRangeText(filter.config.dateRange);
  };

  const getBreakdownNames = () => {
    return (
      compact(
        filter.config.breakdownIds.map(
          (id) => breakdownDimensions.find((d) => d.id === id)?.name,
        ),
      ).join("\n") || "None"
    );
  };

  const renderFilterGroups = () => {
    if (filter.config.breakdownFilters.length === 0) {
      return null;
    }

    return Object.entries(
      groupBy(filter.config.breakdownFilters, "breakdown"),
    ).map(([breakdown, filters]) => (
      <div key={breakdown}>
        {breakdown}:
        <ul className="list-disc pl-6 mt-1">
          {filters.map((f) => (
            <li className="pt-.5" key={f.value}>
              {f.value || "(not set)"}
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  const renderTableMetricIds = () => {
    if (
      !filter.config.tableMetricIds ||
      filter.config.tableMetricIds.length === 0
    ) {
      return null;
    }

    return (
      <div className="flex flex-col gap-3 bg-v2-primary p-3 rounded-[4px] w-[190px] shadow-[0_0_20px_0_rgb(0,0,0,0.025)]">
        <div className="flex flex-col gap-1">
          <div className="text-v2-action border-b border-v2-tertiary pb-1 text-[10px] font-semibold">
            Metrics
          </div>
          <div className="flex flex-col font-light whitespace-pre-wrap">
            <ul className="list-disc pl-6 mt-1">
              {filter.config.tableMetricIds.map((metricId) => {
                // Find the metric using its id
                const foundMetric = metrics.find(
                  (metric) => metric.id === metricId,
                );
                // Display the metric's name if found, otherwise fallback to metricId
                return (
                  <li className="pt-.5" key={metricId}>
                    {foundMetric ? foundMetric.name : metricId}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex gap-1 text-xs text-v2-primary">
        <div className="flex flex-col gap-4 bg-v2-primary p-3 rounded-[4px] w-[190px] shadow-[0_0_20px_0_rgb(0,0,0,0.025)]">
          <div className="flex flex-col gap-1">
            <div className="text-v2-action border-b border-v2-tertiary pb-1 text-[10px] font-semibold">
              Date range
            </div>
            <div className="flex items-center font-light">
              {formatDateRange()}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-v2-action border-b border-v2-tertiary pb-1 text-[10px] font-semibold">
              Comparison period
            </div>
            <div className="flex items-center font-light">Previous period</div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-v2-action border-b border-v2-tertiary pb-1 text-[10px] font-semibold">
              Breakdown
            </div>
            <div className="flex items-center font-light whitespace-pre-wrap">
              {getBreakdownNames()}
            </div>
          </div>
        </div>
        {/* Filters Block */}
        {filter.config.breakdownFilters.length > 0 && (
          <div className="flex flex-col gap-3 bg-v2-primary p-3 rounded-[4px] w-[190px] shadow-[0_0_20px_0_rgb(0,0,0,0.025)]">
            <div className="flex flex-col gap-1">
              <div className="text-v2-action border-b border-v2-tertiary pb-1 text-[10px] font-semibold">
                Filters
              </div>
              <div className="flex flex-col font-light whitespace-pre-wrap">
                {renderFilterGroups()}
              </div>
            </div>
          </div>
        )}
        {/* Metrics Block */}
        {renderTableMetricIds()}
      </div>
    </div>
  );
};
